// Login-Seite (design_handoff_login). Gerendert von App.tsx, solange keine Session existiert; navigiert nicht selbst –
// nach erfolgreicher Anmeldung übernimmt App.tsx über onAuthStateChange (SIGNED_IN).
import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, CheckCircle, EnvelopeSimple, EnvelopeSimpleOpen, LockSimple, PaperPlaneTilt, SignIn } from '@phosphor-icons/react';
import { supabase } from '../lib/supabase/client';
import { getBrowserInfo, MODUS_TITEL, wechsleModus, type LoginModus } from '../lib/login/login';
import { pruefePasswort } from '../lib/profil/profil';
import { useViewport } from '../components/island/hooks';
import { GlasTooltipSchicht } from '../components/ui/GlasTooltip';
import Footer from '../components/Footer';
import { LoginHintergrund, px, useParallax } from './login/Hintergrund';
import { AugeKnopf, Branding, ErfolgsBlock, FehlerBalken, KartenKopf, LoginFeld, PrimaerKnopf, SekundaerKnopf, SslHinweis, ThemeSchalter } from './login/Bausteine';

const RESET_FEHLER = 'Der Link konnte nicht gesendet werden. Bitte versuchen Sie es später erneut.';

interface LoginProps {
  /** „newpw“, wenn App.tsx einen Recovery-Link erkannt hat */
  initialMode?: LoginModus;
  /** Nach „Passwort geändert“ → „Zum Login“: App.tsx verlässt den Recovery-Zustand */
  onRecoveryEnde?: () => void;
}

const Login: React.FC<LoginProps> = ({ initialMode = 'login', onRecoveryEnde }) => {
  // Modus der Karte (README §2): login → reset → sent; newpw/pwdone über den Recovery-Link
  const [modus, setModus] = useState<LoginModus>(initialMode);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Karte schüttelt bei jedem neuen Fehler (README §3)
  const [wackeln, setWackeln] = useState(false);
  const breite = useViewport().width;
  const schmal = breite < 480;
  const buehne = useRef<HTMLDivElement>(null);
  useParallax(buehne);
  const mobil = breite < 900;
  const karte = useRef<HTMLFormElement>(null);
  const letzterModus = useRef(modus);

  // Nach einem Moduswechsel den Fokus in die Karte setzen (erstes Feld, sonst erster Knopf) – nicht beim ersten Anzeigen,
  // damit sich auf dem Handy nicht ungefragt die Tastatur öffnet.
  useEffect(() => {
    if (letzterModus.current === modus) return;
    letzterModus.current = modus;
    const ziel = karte.current?.querySelector<HTMLElement>('input, button[type="submit"], button:not([data-tip^="Verbergen"]):not([data-tip^="Anzeigen"])');
    ziel?.focus({ preventScroll: true });
  }, [modus]);

  const zeigeFehler = (text: string) => {
    setError(text);
    setWackeln(true);
  };

  /** Moduswechsel über die getestete Regel: Fehler weg, zum Login zusätzlich Passwörter leeren. */
  const wechsle = (ziel: LoginModus) => {
    const neu = wechsleModus({ modus, error, password, confirmPassword }, ziel);
    setModus(neu.modus);
    setError(neu.error);
    setPassword(neu.password);
    setConfirmPassword(neu.confirmPassword);
    setWackeln(false);
  };

  // Passwort zurücksetzen: „sent“ wird immer gezeigt (keine Konten-Enumeration); nur technische Fehler als Balken.
  const handleReset = async () => {
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin });
      if (error) throw error;
      wechsle('sent');
    } catch {
      zeigeFehler(RESET_FEHLER);
    } finally {
      setLoading(false);
    }
  };

  // Neues Passwort (Recovery): gleiche Prüfung wie im Profil, danach abmelden – „Zum Login“ zeigt die Anmeldemaske.
  const handleNeuesPasswort = async () => {
    const fehler = pruefePasswort(password, confirmPassword);
    if (fehler) {
      zeigeFehler(fehler);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      await supabase.auth.signOut();
      wechsle('pwdone');
    } catch (err: unknown) {
      zeigeFehler((err as { message?: string } | null)?.message || 'Fehler beim Aktualisieren des Passworts.');
    } finally {
      setLoading(false);
    }
  };

  const zumLogin = () => {
    onRecoveryEnde?.();
    wechsle('login');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      if (data.user) {
        const info = getBrowserInfo(navigator.userAgent);
        await supabase.from('login_history').insert([{
          user_id: data.user.id,
          browser: info.browser,
          os: info.os,
          user_agent: info.ua
        }]);
      }
    } catch {
      zeigeFehler('Anmeldung fehlgeschlagen. Bitte prüfen Sie Ihre Zugangsdaten.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={buehne} className="immo-flaeche flex min-h-screen w-full flex-col items-center justify-center overflow-hidden" style={{ padding: '24px 16px' }}>
      <LoginHintergrund breite={breite} />
      <div className="relative z-[1] flex w-full flex-col items-center" style={{ maxWidth: 420, gap: 22 }}>
        <div className="login-px" style={{ transform: px(-4, -4) }}><Branding /></div>

        {/* Karte mit zwei angedeuteten Karten dahinter (Stapel-Effekt) */}
        <div className="login-px relative w-full" style={{ marginTop: 34, transform: px(-6, -5) }}>
          <div aria-hidden className="absolute inset-0" style={{ borderRadius: 32, background: 'var(--glass)', boxShadow: 'var(--glass-sh)', transform: 'translateY(-62px) scale(.86)', opacity: 0.45 }} />
          <div aria-hidden className="absolute inset-0" style={{ borderRadius: 32, background: 'var(--glass)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', boxShadow: 'var(--glass-sh)', transform: 'translateY(-31px) scale(.93)', opacity: 0.75 }} />
          <form ref={karte} onSubmit={(e) => {
              if (modus === 'reset') { e.preventDefault(); void handleReset(); }
              else if (modus === 'newpw') { e.preventDefault(); void handleNeuesPasswort(); }
              else void handleSubmit(e);
            }} onAnimationEnd={() => setWackeln(false)}
            className={`relative m-0 flex w-full flex-col${wackeln ? ' login-shake' : ''}`}
            style={{ borderRadius: 32, padding: schmal ? '26px 20px 22px' : '34px 32px 26px', gap: 16,
              background: 'var(--glass)', backdropFilter: 'blur(30px) saturate(170%)', WebkitBackdropFilter: 'blur(30px) saturate(170%)', boxShadow: 'var(--glass-sh)' }}>
            <div aria-hidden className="pointer-events-none absolute" style={{ left: 24, right: 24, top: 0, height: 1, background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.7), transparent)' }} />
            <KartenKopf titel={MODUS_TITEL[modus]} />

            {error && <FehlerBalken text={error} />}

            {modus === 'login' && (
              <>
                <LoginFeld label="E-Mail" icon={EnvelopeSimple} typ="email" wert={email} onAendern={setEmail} platzhalter="E-Mail Adresse" autoComplete="username" />
                <LoginFeld label="Passwort" icon={LockSimple} typ={showPassword ? 'text' : 'password'} wert={password} onAendern={setPassword}
                  platzhalter="Passwort" autoComplete="current-password"
                  rechts={<AugeKnopf sichtbar={showPassword} onUmschalten={() => setShowPassword(!showPassword)} mobil={mobil} />} />

                <div className="flex justify-end">
                  <button type="button" onClick={() => wechsle('reset')} data-tip="Zugang vergessen? – Link zum Zurücksetzen des Passworts per E-Mail anfordern"
                    className="hover:!text-[var(--ink)]"
                    style={{ height: mobil ? 44 : 32, padding: '0 6px', border: 'none', cursor: 'pointer', background: 'transparent', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, color: 'var(--acc)' }}>
                    Zugang vergessen?
                  </button>
                </div>

                <PrimaerKnopf icon={ArrowRight} laedt={loading} tip="Login – Mit E-Mail und Passwort anmelden">Login</PrimaerKnopf>
              </>
            )}

            {modus === 'reset' && (
              <>
                <p className="m-0 text-center" style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--ink2)' }}>
                  Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Link, mit dem Sie ein neues Passwort festlegen können.
                </p>
                <LoginFeld label="E-Mail" icon={EnvelopeSimple} typ="email" wert={email} onAendern={setEmail} platzhalter="E-Mail Adresse" autoComplete="username" />
                <PrimaerKnopf icon={PaperPlaneTilt} laedt={loading} tip="Link senden – E-Mail zum Zurücksetzen des Passworts anfordern">Link senden</PrimaerKnopf>
                <SekundaerKnopf icon={ArrowLeft} tip="Zurück zum Login – Anmeldemaske wieder anzeigen" onClick={() => wechsle('login')}>Zurück zum Login</SekundaerKnopf>
              </>
            )}

            {modus === 'sent' && (
              <>
                <ErfolgsBlock icon={EnvelopeSimpleOpen} titel="E-Mail gesendet">
                  Falls ein Konto zu <strong style={{ color: 'var(--ink)' }}>{email}</strong> existiert, erhalten Sie in Kürze einen Link zum Zurücksetzen. Bitte prüfen Sie auch den Spam-Ordner.
                </ErfolgsBlock>
                <SekundaerKnopf icon={ArrowLeft} tip="Zurück zum Login – Anmeldemaske wieder anzeigen" onClick={() => wechsle('login')}>Zurück zum Login</SekundaerKnopf>
              </>
            )}

            {modus === 'newpw' && (
              <>
                <p className="m-0 text-center" style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--ink2)' }}>
                  Legen Sie ein neues Passwort für Ihr Konto fest.
                </p>
                <LoginFeld label="Neues Passwort" icon={LockSimple} typ={showPassword ? 'text' : 'password'} wert={password} onAendern={setPassword}
                  platzhalter="Min. 6 Zeichen" autoComplete="new-password"
                  rechts={<AugeKnopf sichtbar={showPassword} onUmschalten={() => setShowPassword(!showPassword)} mobil={mobil} />} />
                <LoginFeld label="Passwort bestätigen" icon={LockSimple} typ={showPassword ? 'text' : 'password'} wert={confirmPassword} onAendern={setConfirmPassword}
                  platzhalter="Passwort wiederholen" autoComplete="new-password" />
                <PrimaerKnopf icon={Check} laedt={loading} tip="Passwort speichern – Neues Passwort übernehmen">Passwort speichern</PrimaerKnopf>
              </>
            )}

            {modus === 'pwdone' && (
              <>
                <ErfolgsBlock icon={CheckCircle} titel="Passwort geändert">
                  Ihr neues Passwort ist gespeichert. Sie können sich jetzt damit anmelden.
                </ErfolgsBlock>
                <SekundaerKnopf icon={SignIn} tip="Zurück zum Login – Anmeldemaske wieder anzeigen" onClick={zumLogin}>Zum Login</SekundaerKnopf>
              </>
            )}

            <SslHinweis />
          </form>
        </div>

        <Footer />
      </div>
      {/* Nach dem Inhalt im DOM: Tab-Reihenfolge beginnt mit E-Mail (README §7), sichtbar bleibt der Schalter oben rechts. */}
      <div className="fixed z-10" style={{ top: 'max(16px, env(safe-area-inset-top))', right: 16 }}>
        <ThemeSchalter mobil={mobil} />
      </div>
      <GlasTooltipSchicht />
    </div>
  );
};

export default Login;
